import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Key, Money, PencilSimple, Plus, Trash, UserSwitch } from "@phosphor-icons/react";
import api, { apiError } from "../../lib/api";
import { PasswordField, PasswordStrength, passwordStrong } from "../../components/PasswordFields";
import { inr, fmtDate, clientStatusBadge } from "../../lib/format";
import AppLayout, { PageLoader } from "../../components/AppLayout";
import WrmAvatar from "../../components/WrmAvatar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";

const WRM_STATUSES = ["Active", "Inactive", "Suspended"];

const In = ({ label, value, onChange, type = "text", testId, placeholder }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <input type={type} value={value ?? ""} placeholder={placeholder} data-testid={testId}
      onChange={(e) => onChange(e.target.value)} className="glass-input" />
  </div>
);

const Sel = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1.5">
    <label className="label-caps">{label}</label>
    <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
      className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

const EMPTY = { name: "", email: "", mobile: "", username: "", title: "Wealth Relationship Manager", region: "", state: "", city: "", joining_date: "", status: "Active", notes: "", password: "", confirm_password: "" };

const PAY_MONTHS = ["January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];
const PAY_YEARS = [new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1];

const MonthYearSel = ({ form, setForm, prefix }) => (
  <>
    <div className="space-y-1.5">
      <label className="label-caps">Month</label>
      <select value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} data-testid={`${prefix}-month`}
        className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
        <option value="">Select</option>
        {PAY_MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
    </div>
    <div className="space-y-1.5">
      <label className="label-caps">Year</label>
      <select value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid={`${prefix}-year`}
        className="glass-input appearance-none cursor-pointer [&>option]:bg-[#0A110D]">
        {PAY_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  </>
);

function WrmPayDialog({ dlg, onClose }) {
  const [slips, setSlips] = useState(null);
  const [att, setAtt] = useState(null);
  const [slipForm, setSlipForm] = useState({ month: "", year: String(PAY_YEARS[1]), remarks: "" });
  const [attForm, setAttForm] = useState({ month: "", year: String(PAY_YEARS[1]), working_days: "", present: "", absent: "", approved_leave: "", remarks: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    if (!dlg?.user_id) return;
    api.get(`/admin/wrms/${dlg.user_id}/salary-slips`).then((r) => setSlips(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/admin/wrms/${dlg.user_id}/attendance`).then((r) => setAtt(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { setSlips(null); setAtt(null); load(); }, [dlg?.user_id]);

  const uploadSlip = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file || !slipForm.month || !slipForm.year) {
      toast.error("Select month, year and a PDF/image file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("month", slipForm.month);
      fd.append("year", slipForm.year);
      fd.append("remarks", slipForm.remarks);
      await api.post(`/admin/wrms/${dlg.user_id}/salary-slips`, fd);
      toast.success("Salary slip uploaded");
      setSlipForm({ month: "", year: String(PAY_YEARS[1]), remarks: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const deleteSlip = async (s) => {
    if (!window.confirm(`Delete salary slip for ${s.period_label}?`)) return;
    try {
      await api.delete(`/admin/salary-slips/${s.id}`);
      toast.success("Salary slip deleted");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadSlip = async (s) => {
    try {
      const r = await api.get(`/salary-slips/${s.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = s.name || "salary-slip.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveAttendance = async () => {
    const { month, year, working_days, present } = attForm;
    if (!month || !year || working_days === "" || present === "") {
      toast.error("Month, year, working days and present days are required");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/admin/wrms/${dlg.user_id}/attendance`, {
        month: Number(month), year: Number(year), working_days: Number(working_days),
        present: Number(present), absent: Number(attForm.absent || 0),
        approved_leave: Number(attForm.approved_leave || 0), remarks: attForm.remarks || "",
      });
      toast.success("Attendance saved");
      setAttForm({ month: "", year: String(PAY_YEARS[1]), working_days: "", present: "", absent: "", approved_leave: "", remarks: "" });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (!dlg) return null;
  return (
    <Dialog open={!!dlg} onOpenChange={onClose}>
      <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wrm-pay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">Pay & Attendance — {dlg.name}</DialogTitle>
          <DialogDescription className="text-zinc-500">
            Upload monthly salary slips and maintain attendance. Only this WRM can view these records in their My Pay section.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="border border-white/[0.06] rounded-xl p-4 space-y-4">
            <p className="font-heading font-semibold text-sm text-white">Upload Salary Slip</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
              <MonthYearSel form={slipForm} setForm={setSlipForm} prefix="pay-slip" />
              <div className="space-y-1.5">
                <label className="label-caps">File (PDF/JPG/PNG)</label>
                <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" data-testid="pay-slip-file"
                  className="glass-input text-xs file:mr-2 file:rounded-md file:border-0 file:bg-emerald-500/15 file:text-emerald-300 file:px-2 file:py-1" />
              </div>
              <In label="Remarks (optional)" value={slipForm.remarks} onChange={(v) => setSlipForm({ ...slipForm, remarks: v })} testId="pay-slip-remarks" />
            </div>
            <Button onClick={uploadSlip} disabled={busy} data-testid="pay-slip-upload"
              className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
              {busy ? "Uploading..." : "Upload Slip"}
            </Button>
            <div data-testid="pay-slip-list">
              {(slips || []).map((s) => (
                <div key={s.id} className="flex items-center gap-3 border border-white/[0.06] bg-white/[0.02] rounded-lg px-4 py-2.5 mt-2"
                  data-testid={`pay-slip-row-${s.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white">{s.period_label}</p>
                    <p className="text-[11px] text-zinc-500 truncate">{s.name} · {s.size_kb} KB · {fmtDate(s.uploaded_at)}{s.remarks ? ` · ${s.remarks}` : ""}</p>
                  </div>
                  <button onClick={() => downloadSlip(s)} data-testid={`pay-slip-dl-${s.id}`}
                    className="h-8 w-8 rounded-lg border border-white/10 flex items-center justify-center text-zinc-400 hover:text-emerald-300 transition-colors duration-150">
                    <Download size={13} />
                  </button>
                  <button onClick={() => deleteSlip(s)} data-testid={`pay-slip-del-${s.id}`}
                    className="h-8 w-8 rounded-lg border border-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-colors duration-150">
                    <Trash size={13} />
                  </button>
                </div>
              ))}
              {slips && slips.length === 0 && <p className="text-xs text-zinc-600 mt-2">No salary slips uploaded yet.</p>}
            </div>
          </div>

          <div className="border border-white/[0.06] rounded-xl p-4 space-y-4">
            <p className="font-heading font-semibold text-sm text-white">Monthly Attendance</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <MonthYearSel form={attForm} setForm={setAttForm} prefix="pay-att" />
              <In label="Working Days" type="number" value={attForm.working_days} onChange={(v) => setAttForm({ ...attForm, working_days: v })} testId="pay-att-working" />
              <In label="Days Present" type="number" value={attForm.present} onChange={(v) => setAttForm({ ...attForm, present: v })} testId="pay-att-present" />
              <In label="Days Absent" type="number" value={attForm.absent} onChange={(v) => setAttForm({ ...attForm, absent: v })} testId="pay-att-absent" />
              <In label="Approved Leave" type="number" value={attForm.approved_leave} onChange={(v) => setAttForm({ ...attForm, approved_leave: v })} testId="pay-att-leave" />
            </div>
            <In label="Remarks (optional)" value={attForm.remarks} onChange={(v) => setAttForm({ ...attForm, remarks: v })} testId="pay-att-remarks" />
            <Button onClick={saveAttendance} disabled={busy} data-testid="pay-att-save"
              className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold">
              {busy ? "Saving..." : "Save Attendance"}
            </Button>
            <div data-testid="pay-att-list">
              {(att || []).map((a) => (
                <div key={a.id} className="flex items-center justify-between border border-white/[0.06] bg-white/[0.02] rounded-lg px-4 py-2.5 mt-2"
                  data-testid={`pay-att-row-${a.id}`}>
                  <p className="text-sm font-medium text-white">{a.period_label}</p>
                  <p className="tnum text-xs text-zinc-400">
                    {a.present}/{a.working_days} present · <span className="text-emerald-300 font-semibold">{a.attendance_pct}%</span>
                  </p>
                </div>
              ))}
              {att && att.length === 0 && <p className="text-xs text-zinc-600 mt-2">No attendance records yet.</p>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminWrms() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [dlg, setDlg] = useState(null);
  const [resetDlg, setResetDlg] = useState(null);
  const [payDlg, setPayDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const [photoKey, setPhotoKey] = useState(0);

  const uploadWrmPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !dlg?.user_id) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/wrms/${dlg.user_id}/photo`, fd);
      toast.success("WRM photo updated");
      setPhotoKey((k) => k + 1);
      load();
    } catch (err) { toast.error(apiError(err)); }
    e.target.value = "";
  };

  const removeWrmPhoto = async () => {
    if (!dlg?.user_id || !window.confirm("Remove WRM photo?")) return;
    try {
      await api.delete(`/admin/wrms/${dlg.user_id}/photo`);
      toast.success("WRM photo removed");
      setPhotoKey((k) => k + 1);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const load = () => api.get("/admin/wrms").then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!dlg.id && (!dlg.username || !passwordStrong(dlg.password) || dlg.password !== dlg.confirm_password)) {
      toast.error("Login credentials: enter a unique Login ID and a strong matching password (8+ chars, at least one letter and number).");
      return;
    }
    setBusy(true);
    try {
      if (dlg.id) {
        await api.patch(`/admin/wrms/${dlg.user_id}`, {
          name: dlg.name, email: dlg.email, mobile: dlg.mobile, title: dlg.title, region: dlg.region,
          state: dlg.state, city: dlg.city, joining_date: dlg.joining_date || null,
          status: dlg.status, notes: dlg.notes, username: dlg.username || undefined,
        });
        toast.success("WRM updated");
      } else {
        await api.post("/admin/wrms", {
          name: dlg.name, email: dlg.email, mobile: dlg.mobile, title: dlg.title,
          region: dlg.region, state: dlg.state, city: dlg.city,
          joining_date: dlg.joining_date || null, status: dlg.status, notes: dlg.notes,
          username: dlg.username, password: dlg.password,
        });
        toast.success("WRM created — login credentials active");
      }
      setDlg(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleStatus = async (w) => {
    const next = w.status === "Active" ? "Inactive" : "Active";
    try {
      await api.patch(`/admin/wrms/${w.user_id}`, { status: next });
      toast.success(next === "Inactive" ? "WRM deactivated — login blocked, records preserved" : "WRM reactivated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const submitReset = async () => {
    if (!passwordStrong(resetDlg.password) || resetDlg.password !== resetDlg.confirm_password) {
      toast.error("Enter a strong matching password (8+ chars, at least one letter and number).");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${resetDlg.user_id}/reset-password`, { new_password: resetDlg.password });
      toast.success(`Password updated for ${resetDlg.name} — audit logged.`);
      setResetDlg(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (error) return <AppLayout title="WRMs"><p className="text-red-400 text-sm">{error}</p></AppLayout>;
  if (!rows) return <AppLayout title="WRMs"><PageLoader /></AppLayout>;

  return (
    <AppLayout title="Wealth Relationship Managers" subtitle={`${rows.length} relationship managers`}>
      <div className="fade-up space-y-5">
        <div className="flex justify-end">
          <Button data-testid="add-wrm-button" onClick={() => setDlg({ ...EMPTY })}
            className="bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold emerald-glow">
            <Plus size={16} className="mr-1.5" weight="bold" /> Add WRM
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="wrm-cards">
          {rows.map((w) => (
            <div key={w.id} className="glass-card glass-card-hover p-7" data-testid={`wrm-card-${w.user_id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <WrmAvatar key={`${photoKey}-${w.user_id}`} userId={w.user_id} name={w.name} size={56} hasPhoto={w.has_photo} testId={`wrm-photo-${w.user_id}`} />
                  <div>
                    <p className="font-display text-xl text-white">{w.name}</p>
                    <p className="tnum text-[11px] text-zinc-500 mt-0.5">{w.wrm_code || w.user_id.slice(0, 8)}{w.username ? ` · ${w.username}` : ""}</p>
                    <p className="text-xs text-zinc-500">{w.title}</p>
                  </div>
                </div>
                <Badge variant="outline" className={`text-[10px] ${clientStatusBadge(w.status)}`} data-testid={`wrm-status-${w.user_id}`}>
                  {w.status}
                </Badge>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-6">
                <div>
                  <p className="label-caps">Clients</p>
                  <p className="tnum text-2xl text-white mt-1.5" data-testid={`wrm-clients-${w.user_id}`}>{w.client_count}</p>
                </div>
                <div className="col-span-2">
                  <p className="label-caps">Assigned AUM</p>
                  <p className="tnum text-2xl text-white mt-1.5" data-testid={`wrm-aum-${w.user_id}`}>{inr(w.aum)}</p>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-white/[0.06] grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-zinc-500">
                <p>{w.email}</p>
                <p>{w.phone}</p>
                <p>{[w.city, w.state].filter(Boolean).join(", ") || w.region || "—"}</p>
                <p>Joined {fmtDate(w.joining_date)}</p>
              </div>
              {w.notes && <p className="text-[11px] text-zinc-600 mt-3 italic">{w.notes}</p>}
              <div className="flex gap-2 mt-5">
                <Button size="sm" variant="outline" data-testid={`wrm-edit-${w.user_id}`}
                  onClick={() => setDlg({ ...w, mobile: w.phone })}
                  className="border-white/10 text-zinc-300 hover:bg-white/5">
                  <PencilSimple size={13} className="mr-1.5" /> Edit
                </Button>
                <Button size="sm" variant="outline" data-testid={`wrm-reset-pw-${w.user_id}`}
                  onClick={() => setResetDlg({ user_id: w.user_id, name: w.name, password: "", confirm_password: "" })}
                  className="border-white/10 text-zinc-300 hover:bg-white/5">
                  <Key size={13} className="mr-1.5" /> Reset Password
                </Button>
                <Button size="sm" variant="outline" data-testid={`wrm-pay-${w.user_id}`}
                  onClick={() => setPayDlg({ user_id: w.user_id, name: w.name })}
                  className="border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/10">
                  <Money size={13} className="mr-1.5" /> Pay
                </Button>
                <Button size="sm" variant="outline" data-testid={`wrm-toggle-${w.user_id}`} onClick={() => toggleStatus(w)}
                  className={w.status === "Active"
                    ? "border-red-500/25 text-red-400 hover:bg-red-500/10"
                    : "border-emerald-500/25 text-emerald-300 hover:bg-emerald-500/10"}>
                  <UserSwitch size={13} className="mr-1.5" /> {w.status === "Active" ? "Deactivate" : "Activate"}
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={() => setDlg(null)}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-xl max-h-[90vh] overflow-y-auto" data-testid="wrm-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">{dlg?.id ? "Edit WRM" : "Add WRM"}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {dlg?.id ? "Update WRM details. Client assignments are preserved." : "Creates a secure WRM login account with the WRM role."}
            </DialogDescription>
          </DialogHeader>
          {dlg && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              <In label="Full Name" value={dlg.name} onChange={(v) => setDlg({ ...dlg, name: v })} testId="w-name" />
              <In label="Email" type="email" value={dlg.email} onChange={(v) => setDlg({ ...dlg, email: v })} testId="w-email" />
              <In label="Mobile" value={dlg.mobile} onChange={(v) => setDlg({ ...dlg, mobile: v })} testId="w-mobile" />
              <In label="Title" value={dlg.title} onChange={(v) => setDlg({ ...dlg, title: v })} testId="w-title" />
              <In label="Region" value={dlg.region} onChange={(v) => setDlg({ ...dlg, region: v })} testId="w-region" />
              <In label="State" value={dlg.state} onChange={(v) => setDlg({ ...dlg, state: v })} testId="w-state" />
              <In label="City" value={dlg.city} onChange={(v) => setDlg({ ...dlg, city: v })} testId="w-city" />
              <In label="Joining Date" type="date" value={dlg.joining_date} onChange={(v) => setDlg({ ...dlg, joining_date: v })} testId="w-joining" />
              <Sel label="Status" value={dlg.status} onChange={(v) => setDlg({ ...dlg, status: v })} options={WRM_STATUSES} testId="w-status" />
              <In label="WRM Login User ID" value={dlg.username} onChange={(v) => setDlg({ ...dlg, username: v })} placeholder="WRM-SACHIN-001" testId="w-username" />
              {!dlg.id && (
                <>
                  <PasswordField label="WRM Password" value={dlg.password} onChange={(v) => setDlg({ ...dlg, password: v })} testId="w-password" />
                  <PasswordField label="Confirm Password" value={dlg.confirm_password} onChange={(v) => setDlg({ ...dlg, confirm_password: v })} testId="w-confirm-password" />
                  <div className="sm:col-span-2 space-y-1.5">
                    <PasswordStrength password={dlg.password} testId="w-password-strength" />
                    {dlg.confirm_password && dlg.password !== dlg.confirm_password && (
                      <p className="text-[11px] text-red-400" data-testid="w-password-mismatch">Passwords do not match.</p>
                    )}
                  </div>
                </>
              )}
              <div className="sm:col-span-2">
                <In label="Notes" value={dlg.notes} onChange={(v) => setDlg({ ...dlg, notes: v })} testId="w-notes" />
              </div>
              {dlg.id && (
                <div className="sm:col-span-2 border-t border-white/[0.06] pt-4">
                  <label className="label-caps">WRM Profile Image</label>
                  <div className="flex items-center gap-4 mt-3">
                    <WrmAvatar key={`dlg-${photoKey}-${dlg.user_id}`} userId={dlg.user_id} name={dlg.name} size={56} testId="w-photo-preview" />
                    <input ref={photoRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                      data-testid="w-photo-input" onChange={uploadWrmPhoto} />
                    <Button size="sm" variant="outline" data-testid="w-photo-upload"
                      onClick={() => photoRef.current?.click()}
                      className="border-white/10 text-zinc-300 hover:bg-white/5">
                      Upload / Replace
                    </Button>
                    {dlg.has_photo && (
                      <Button size="sm" variant="outline" data-testid="w-photo-remove" onClick={removeWrmPhoto}
                        className="border-red-500/25 text-red-400 hover:bg-red-500/10">
                        Remove
                      </Button>
                    )}
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-2">PNG/JPG/WebP, up to 5 MB. Shown on the WRM dashboard, client portal and admin list.</p>
                </div>
              )}
            </div>
          )}
          <Button onClick={submit} disabled={busy} data-testid="wrm-save"
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold mt-4">
            {busy ? "Saving..." : dlg?.id ? "Save Changes" : "Create WRM"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetDlg} onOpenChange={() => setResetDlg(null)}>
        <DialogContent className="border-white/10 bg-[#0A110D]/95 backdrop-blur-2xl max-w-md" data-testid="wrm-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-white">Reset WRM Password</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Sets a new portal password for {resetDlg?.name}. The current password is never shown.
            </DialogDescription>
          </DialogHeader>
          {resetDlg && (
            <div className="space-y-4">
              <PasswordField label="New Password" value={resetDlg.password} onChange={(v) => setResetDlg({ ...resetDlg, password: v })} testId="rw-password" />
              <PasswordField label="Confirm Password" value={resetDlg.confirm_password} onChange={(v) => setResetDlg({ ...resetDlg, confirm_password: v })} testId="rw-confirm" />
              <PasswordStrength password={resetDlg.password} testId="rw-strength" />
              {resetDlg.confirm_password && resetDlg.password !== resetDlg.confirm_password && (
                <p className="text-[11px] text-red-400" data-testid="rw-mismatch">Passwords do not match.</p>
              )}
            </div>
          )}
          <Button onClick={submitReset} disabled={busy} data-testid="rw-submit"
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#030706] font-bold mt-2">
            {busy ? "Updating..." : "Reset Password"}
          </Button>
        </DialogContent>
      </Dialog>

      <WrmPayDialog dlg={payDlg} onClose={() => setPayDlg(null)} />
    </AppLayout>
  );
}
